/**
 * The GOAL LEDGER — a goal's own account of what happened to it, with no React and no imports.
 *
 * A goal shows you where it stands (the ring, the plan, the members) and nothing at all about how it got
 * there. Every question the agent put to you, every answer you gave, every time the plan was re-cut
 * underneath you: all of it happened and none of it was readable. This module is the rules for reading it
 * back — the same three-surface contract iOS `GoalLedger.swift` and Android `GoalLedger.kt` implement.
 *
 * Five decisions worth knowing before you change anything here:
 *
 * **1. A decision and the plan change it caused are ONE entry, not two.** "We asked whether to push the
 * launch a week, you said yes" and "two prep tasks moved" are one event told twice, and a log that tells
 * it twice reads as a database dump instead of a story. `buildGoalLedger` folds attributable changes into
 * their decision's entry and only emits a standalone `change` entry for a change no decision explains.
 *
 * **2. Attribution is by TIME, because the data carries no causal link — and the copy never claims more
 * than that.** `Decision.planVersion` is the version a question was *raised against*; neither
 * `AgentTaskChange` nor `Goal` records a version at all, so there is no id and no version to join on.
 * What is true is that the agent settles a question and re-cuts the plan in the SAME run, seconds apart.
 * So a change lands in a decision's entry when it falls in the window that run occupies
 * (`PLAN_REVISION_WINDOW_MS` after the settlement), and the copy says the plan moved *with* it — an
 * adjacency we can see — never that the decision *caused* it, which we cannot.
 *
 * **3. An OPEN decision is not history.** It has not happened; it is a thing being asked of you right
 * now. It never enters `entries` — it comes back in `open`, for the caller to render as something to act
 * on. A pending question filed under "what happened" is both a lie and a dead end, since the reader can
 * see it and not answer it. A decision whose status this build cannot read fails INTO `open` for the same
 * reason: "we might still need you" is recoverable, "this is settled" is not.
 *
 * **4. No enum ever reaches the screen.** Every status, kind, resolution and change label leaves here as
 * an i18n KEY, and an unrecognised change label returns no key at all so the caller renders the agent's
 * own words instead of `review:changes-requested`. `goal-ledger.test.ts` fails if any of them leaks.
 *
 * **5. An entry is a SENTENCE, not a document — and not every change is goal history.** A task's change
 * log also carries the agent's correspondence with its own reviewer: `submit_for_review` writes the
 * deliverables it is handing over, and `review` writes the reviewer's verification notes. Those are two
 * agents talking to each other about one task's execution, in markdown, at whatever length the model
 * felt like — one live entry was 3,693 characters of "here is what I verified against the brief", which
 * pushed the plan off the screen. So `isLedgerVisibleChange` drops the two round-trip labels that leave
 * the task exactly where it was, `ledgerChangeText` refuses to render the reviewer's prose at all, and
 * what does render goes through `plainLedgerText` (markdown STRIPPED, never shown raw) and
 * `clampLedgerText` (capped, and the cap says so). See those four for the reasoning.
 *
 * Free of the APP's dependency graph on purpose, exactly like `lib/goal-rounds.ts`: orion-web's jest
 * config cannot parse the ESM common-lib pulls in, so a unit test may not reach it, and enum values are
 * string literals rather than reads off the entities barrel (a module-level constant that reads an enum
 * through a barrel evaluates before the barrel initialises). The one import is `./strip-markdown`, which
 * imports nothing itself and is already unit-tested under this same jest config — the alternative was a
 * fourth copy of a stripper that already exists on all three surfaces.
 */

import { stripMarkdown } from './strip-markdown';

/** Who settled a decision. Only the two the service can record; absent reads as the owner — see below. */
export interface WebLedgerVerdict {
  /** `ACCEPTED` | `REFUSED`. Absent when the decision ended without one (expired, withdrawn). */
  resolution?: string;
  /** The chosen option's LABEL, resolved from `options` by the route — never a raw option id. */
  choice?: string;
  /** Free text, for an ANSWER decision. */
  answer?: string;
  /** `ACCOUNT_OWNER` | `COLLABORATOR`. Absent reads as the owner: `Rule.OWNER` is the only settlement
   *  rule the service implements, so an unattributed verdict is the owner's by construction. */
  principal?: string;
  /** ISO. When the verdict was cast. */
  at?: string;
}

/** One decision on this goal, as the ledger route serves it. */
export interface WebLedgerDecision {
  id: string;
  /** The question, in the user's own language — the agent wrote it, so it is the entry's headline. */
  question?: string;
  /** Optional markdown context the card can reveal rather than headline. */
  detail?: string;
  /** `APPROVE` | `CHOOSE` | `ANSWER` | `CONFIRM`. Decides which sentence the outcome reads as. */
  kind?: string;
  /** `OPEN` | `SETTLED` | `EXPIRED` | `WITHDRAWN`. Anything else fails into `open`. */
  status?: string;
  /** ISO. When the question was raised. */
  raisedDate?: string;
  /** ISO. When it settled, if it did. */
  settledDate?: string;
  /** ISO. When an answer is needed by — the only thing an OPEN entry can say about urgency. */
  dueBy?: string;
  /** How it ended. Absent on an open decision, and on one that expired or was withdrawn. */
  outcome?: WebLedgerVerdict;
  /**
   * The alternatives, for a CHOOSE — id AND label, because the button carries the label and the verdict
   * carries the id. Distinct from `outcome.choice`, which is the LABEL of the option that already won.
   *
   * Absent on every other kind, and on a CHOOSE authored with none — which is unanswerable, not empty.
   */
  options?: WebLedgerOption[];
  /**
   * The plan version the question was RAISED against — the value a verdict must echo back verbatim.
   *
   * `CastVerdict` compares the verdict's version with the decision's and refuses a mismatch, so this is
   * the whole staleness mechanism and it only works client-side. Absent means the goal had never been
   * revised, and it must be echoed back ABSENT: the service compares `Optional<Long>`s, so a `"0"`
   * mismatches an empty one and refuses a perfectly good answer.
   */
  planVersion?: string;
}

/** One alternative a CHOOSE offers, as a client must render AND answer it. */
export interface WebLedgerOption {
  /** The stable handle. This — never the label — is what a verdict's `choice` carries back. */
  id: string;
  /** What the user reads on the button, in their language. */
  label: string;
}

/** One entry from a task's append-only change log, carrying the task it belongs to. */
export interface WebLedgerChange {
  /** Stable within a goal: the task id plus this entry's index in that task's log. */
  id: string;
  taskId: string;
  /** The task's title, so a change reads as "Book the venue — called off" and not as a bare sentence. */
  taskTitle: string;
  /** The raw vocabulary label. NEVER rendered; `changeLabelKey` turns it into a key or into nothing. */
  label?: string;
  /** The agent's own words about what changed. This is the sentence the reader actually wants. */
  content?: string;
  /** ISO. */
  date?: string;
}

/** What the ledger route serves for one goal. */
export interface WebGoalLedger {
  decisions: WebLedgerDecision[];
  changes: WebLedgerChange[];
}

/** One thing that happened, or one open question. */
export type LedgerEntry =
  | {
      kind: 'decision';
      /** Sort key, ms. */
      at: number;
      decision: WebLedgerDecision;
      /** The plan changes that landed alongside this settlement. Empty when none did. */
      changes: WebLedgerChange[];
    }
  | { kind: 'change'; at: number; change: WebLedgerChange };

/** What the ledger section should render. `absent` and `unavailable` are NOT the same claim. */
export type LedgerView =
  | { kind: 'unavailable' }
  | {
      kind: 'ledger';
      /** Questions still waiting on somebody. Never history; rendered above it, as something to act on. */
      open: WebLedgerDecision[];
      /** Newest first, capped at `LEDGER_LIMIT`. */
      entries: LedgerEntry[];
      /** True when the cap cut entries off. The UI MUST say so — a silent cut reads as "that is all". */
      truncated: boolean;
    };

/**
 * How long after a settlement a plan change still reads as having moved WITH it.
 *
 * The agent casts the verdict and re-cuts the plan inside one run: the two writes are seconds apart, and
 * fifteen minutes is generous cover for a slow run without reaching into the next hour's unrelated work.
 * Widening this does not make the ledger more accurate — it makes it claim more. If the entity ever
 * carries a real link (a `decisionId` on the change, or a plan version on both sides), delete the window
 * and join on it; that is strictly better and this constant is the placeholder for it.
 */
export const PLAN_REVISION_WINDOW_MS = 15 * 60 * 1000;

/** How many entries the panel shows. A goal that ran for a year is not a scrollback buffer. */
export const LEDGER_LIMIT = 20;

/**
 * How much of one change's text a row renders before it is cut.
 *
 * An entry is a sentence. This is about two lines of a nested block at the ledger's type size, which is
 * enough for the agent's own account of what changed ("Moved to the 14th; the caterer confirmed") and
 * nowhere near enough for a document. The rest is not thrown away — `truncated` is reported so the row
 * can offer `common.expand`, and a reader who wants the whole thing gets the whole thing.
 */
export const LEDGER_TEXT_LIMIT = 240;

const OPEN_STATUS = 'OPEN';
const HISTORY_STATUSES = new Set(['SETTLED', 'EXPIRED', 'WITHDRAWN']);

const ms = (iso: string | undefined): number | undefined => {
  if (!iso) return undefined;
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : undefined;
};

/**
 * Is this question still waiting on somebody?
 *
 * Fails INTO open. `OPEN` is open; the three terminal statuses are history; anything else — absent, an
 * `UNKNOWN_STATUS`, a status added after this build shipped — is open unless it carries a settlement
 * date, because filing a live question under "what happened" both misinforms the reader and strands them
 * (history is not actionable). The reverse mistake costs a stale row in an inbox.
 */
export function isOpenDecision(decision: WebLedgerDecision): boolean {
  const status = decision.status?.trim().toUpperCase();
  if (status === OPEN_STATUS) return true;
  if (status && HISTORY_STATUSES.has(status)) return false;
  return !decision.settledDate;
}

/** When a settled decision happened. Its settlement, else its verdict, else when it was raised. */
export function decisionAt(decision: WebLedgerDecision): number | undefined {
  return ms(decision.settledDate) ?? ms(decision.outcome?.at) ?? ms(decision.raisedDate);
}

/**
 * Build the ledger.
 *
 * Order of operations matters: open decisions are removed first (they can never claim a plan change —
 * nothing has been settled to cause one), then each change is attributed to the NEAREST PRECEDING
 * settlement within the window, so two decisions settled minutes apart do not both claim the same change.
 * A change with no readable date is never attributed and never dropped: it becomes its own entry, sorted
 * to the bottom, because a change we cannot place in time is still a change that happened.
 */
export function buildGoalLedger(ledger: WebGoalLedger, limit: number = LEDGER_LIMIT): LedgerView {
  const open: WebLedgerDecision[] = [];
  const settled: { decision: WebLedgerDecision; at: number }[] = [];
  for (const decision of ledger.decisions) {
    if (isOpenDecision(decision)) {
      open.push(decision);
      continue;
    }
    const at = decisionAt(decision);
    // A settled decision with no readable instant cannot be placed on a timeline and cannot own a
    // change; it still happened, so it is kept at the far end rather than dropped.
    settled.push({ decision, at: at ?? 0 });
  }
  // Ascending, so "the nearest settlement at or before this change" is a scan backwards from the end.
  const ordered = [...settled].sort((a, b) => a.at - b.at);
  const claimed = new Map<string, WebLedgerChange[]>();
  const unclaimed: WebLedgerChange[] = [];

  for (const change of ledger.changes) {
    // Not goal history at all — a hand-off between the working agent and its reviewer. Dropped BEFORE
    // attribution and before the cap, so the twenty entries a panel shows are twenty real ones.
    if (!isLedgerVisibleChange(change.label)) continue;
    const at = ms(change.date);
    const owner = at === undefined ? undefined : nearestSettlement(ordered, at);
    if (owner) {
      const bucket = claimed.get(owner.decision.id);
      if (bucket) bucket.push(change);
      else claimed.set(owner.decision.id, [change]);
    } else {
      unclaimed.push(change);
    }
  }

  const entries: LedgerEntry[] = [
    ...settled.map<LedgerEntry>(({ decision, at }) => ({
      kind: 'decision',
      at,
      decision,
      // Oldest first inside one entry: the plan moved in that order, and a reversed list inside a
      // chronological list is the kind of detail that makes a log unreadable.
      changes: (claimed.get(decision.id) ?? []).sort((a, b) => (ms(a.date) ?? 0) - (ms(b.date) ?? 0)),
    })),
    ...unclaimed.map<LedgerEntry>((change) => ({ kind: 'change', at: ms(change.date) ?? 0, change })),
  ].sort((a, b) => b.at - a.at);

  const capped = limit > 0 ? entries.slice(0, limit) : entries;
  // Open questions are sorted by what the reader is being asked to hurry: soonest deadline first, then
  // the ones with no deadline, oldest first — those have been waiting longest.
  open.sort((a, b) => (ms(a.dueBy) ?? Number.MAX_SAFE_INTEGER) - (ms(b.dueBy) ?? Number.MAX_SAFE_INTEGER) || (ms(a.raisedDate) ?? 0) - (ms(b.raisedDate) ?? 0));
  return { kind: 'ledger', open, entries: capped, truncated: capped.length < entries.length };
}

/** The latest settlement at or before `at` whose window still covers it, or nothing. */
function nearestSettlement(ascending: { decision: WebLedgerDecision; at: number }[], at: number) {
  for (let i = ascending.length - 1; i >= 0; i -= 1) {
    const candidate = ascending[i];
    if (candidate.at > at) continue;
    return at - candidate.at <= PLAN_REVISION_WINDOW_MS ? candidate : undefined;
  }
  return undefined;
}

/**
 * The sentence a settled decision reads as — "You approved it.", "You chose the Tuesday slot."
 *
 * Two families, `you` and a collaborator, because "You approved it" printed over somebody else's approval
 * is a lie about the reader's own history. An unattributed verdict takes the `you` family deliberately:
 * `Rule.OWNER` is the only settlement rule the service implements, so a verdict with no principal on it
 * is the owner's — and the owner IS the reader.
 *
 * Returns a key plus the params it needs, so no call site composes a sentence out of fragments.
 */
export function decisionOutcomeCopy(decision: WebLedgerDecision): { key: string; params?: Record<string, string> } {
  const status = decision.status?.trim().toUpperCase();
  if (status === 'EXPIRED') return { key: 'goals.ledger.expired' };
  if (status === 'WITHDRAWN') return { key: 'goals.ledger.withdrawn' };

  const outcome = decision.outcome;
  const byOther = outcome?.principal?.trim().toUpperCase() === 'COLLABORATOR';
  const family = byOther ? 'goals.ledger.settled.byOther' : 'goals.ledger.settled';
  const resolution = outcome?.resolution?.trim().toUpperCase();
  const kind = decision.kind?.trim().toUpperCase();

  if (resolution === 'REFUSED') return { key: `${family}.declined` };
  if (resolution !== 'ACCEPTED') return { key: 'goals.ledger.settled.plain' };

  // An accepted CHOOSE/ANSWER only reads as one when it actually carries the words. Without them the
  // sentence would be "You chose ." — the neutral "You agreed." is the honest fallback.
  const choice = outcome?.choice?.trim();
  if (kind === 'CHOOSE' && choice) return { key: `${family}.chose`, params: { choice } };
  const answer = outcome?.answer?.trim();
  if (kind === 'ANSWER' && answer) return { key: `${family}.answered`, params: { answer } };
  if (kind === 'APPROVE') return { key: `${family}.approved` };
  if (kind === 'CONFIRM') return { key: `${family}.confirmed` };
  return { key: `${family}.agreed` };
}

/**
 * The i18n key naming a change, or NOTHING.
 *
 * Nothing is the important half. The label vocabulary is the harness's (`AGENT_TASK_CHANGE_LABEL`) plus
 * an agent-name-prefixed `<Agent>-completion`, and it grows without this file: a label added there and
 * rendered raw here puts `review:changes-requested` on a user's screen in every locale. So an unknown
 * label returns `undefined` and the caller shows the change's CONTENT — the agent's own sentence, which
 * is what the reader wanted anyway.
 */
export function changeLabelKey(label: string | undefined): string | undefined {
  const value = label?.trim();
  if (!value) return undefined;
  if (value.endsWith('-completion')) return 'goals.ledger.change.completion';
  switch (value) {
    case 'submission':
      return 'goals.ledger.change.submitted';
    case 'review:approved':
      return 'goals.ledger.change.reviewApproved';
    case 'review:changes-requested':
      return 'goals.ledger.change.reviewChanges';
    case 'review:failed':
      return 'goals.ledger.change.reviewFailed';
    case 'failed':
      return 'goals.ledger.change.failed';
    case 'note':
      return 'goals.ledger.change.note';
    case 'closed:user-directed':
      return 'goals.ledger.change.closedByUser';
    case 'cancelled':
      return 'goals.ledger.change.cancelled';
    case 'superseded':
      return 'goals.ledger.change.superseded';
    case 'system:reported-success':
      return 'goals.ledger.change.reportedSuccess';
    case 'action-needed':
      return 'goals.ledger.change.actionNeeded';
    default:
      return undefined;
  }
}

/**
 * A change's text, ready to render: what to show, what an expanded row shows, and whether it was cut.
 *
 * `text` is never raw markdown and never unbounded. `full` is the whole thing, still plain — expanding a
 * row must not swap prose for asterisks.
 */
export interface LedgerText {
  /** What the row renders collapsed. Plain, and at most `LEDGER_TEXT_LIMIT` characters. */
  text: string;
  /** The whole thing, plain. Equal to `text` when nothing was cut. */
  full: string;
  /** True when `text` is a cut of `full`. The UI MUST offer the rest — a silent cut reads as "that is
   *  all there was", the same lie `LedgerView.truncated` exists to prevent one level up. */
  truncated: boolean;
}

/**
 * The agent↔reviewer round trip, by label.
 *
 * `submit_for_review` appends the deliverables the working agent is handing over; `review` appends the
 * reviewer's verdict prose. Both are correspondence between two agents about one task's execution —
 * addressed to each other, not to the reader — and the reviewer's half in particular is a verification
 * transcript: boot sequences, tool ids, "so this is cosmetic, not a gap".
 */
const REVIEW_CORRESPONDENCE_LABELS = new Set(['submission', 'review:approved', 'review:changes-requested', 'review:failed']);

/**
 * The round-trip labels that are not goal history AT ALL.
 *
 * These two leave the task exactly where it was: `submission` hands the work to a reviewer (still open,
 * still being worked on) and `review:changes-requested` hands it back (likewise). Nothing about the goal
 * changed, so an entry for either is a row that reports a hand-off between two agents — which is what
 * made the ledger read as a database dump of somebody else's job.
 *
 * The two TERMINAL verdicts are deliberately NOT here. `review:approved` transitions the task to
 * COMPLETED and `review:failed` to FAILED (`manage-task-tool.ts`, `review-approve` / `review-fail`), and
 * they are the ONLY entry the log ever gets for that ending — dropping them would erase the record that
 * a task finished, which is exactly the class of silent loss this feature exists to undo. They stay, and
 * `ledgerChangeText` drops their prose instead, so the row reads as "Prep the venue — Review passed".
 */
const INTERNAL_ROUND_TRIP_LABELS = new Set(['submission', 'review:changes-requested']);

/** Does this change belong in a GOAL's history at all? See `INTERNAL_ROUND_TRIP_LABELS`. */
export function isLedgerVisibleChange(label: string | undefined): boolean {
  const value = label?.trim();
  return !value || !INTERNAL_ROUND_TRIP_LABELS.has(value);
}

/** Is this change's own text correspondence with a reviewer rather than an account for the reader? */
export function isReviewCorrespondence(label: string | undefined): boolean {
  const value = label?.trim();
  return !!value && REVIEW_CORRESPONDENCE_LABELS.has(value);
}

/**
 * Agent-authored markdown → one line of plain prose.
 *
 * **Stripped, not rendered, and that is a decision.** The repo has a real markdown renderer (`Markdown`
 * on web, `MarkdownBlocks` / `MarkdownColumn` on the phones) and it is the right answer for a document
 * surface. A ledger row is not one: it is a two-line record inside a nested block, next to a date, one
 * of twenty. Rendering headings, lists and code fences there would make one entry taller than the plan
 * it describes, and the row is capped anyway — a heading with its first four words is worse typography
 * than a sentence with its first four words. What it must never do is show `**Brief requirements**`
 * with its asterisks, which is what shipped.
 *
 * The stripping itself is NOT new code: `stripMarkdown` is this repo's established markdown→plain-text
 * reduction for surfaces with no renderer (notification bodies, clamped teasers), and it already has
 * twins on both phones — iOS `markdownPlainText`, Android `markdownToPlainText`. All this adds is the
 * flattening to ONE line, because those two keep newlines for a multi-line teaser and a ledger row is a
 * sentence. A fourth stripper would have been a fourth thing to keep in step.
 */
export function plainLedgerText(raw: string | undefined): string {
  if (!raw) return '';
  // `stripMarkdown` already collapses whitespace; the phones' twins keep `\n`, so both do this and the
  // three stay honest ports of one another.
  return stripMarkdown(raw).replace(/\s+/g, ' ').trim();
}

/** Trailing punctuation a cut leaves dangling, so "…the run, …" does not ship. */
const TRAILING_CUT_CHARS = ' \t.,;:!?-\u2013\u2014\u2026';

/**
 * Cut to `limit`, on a word boundary, and SAY it was cut.
 *
 * The boundary is only honoured while it still leaves most of the allowance — a 240-character cap that
 * lands mid-URL would otherwise back off to 20 characters and the row would say nothing at all.
 */
export function clampLedgerText(text: string, limit: number = LEDGER_TEXT_LIMIT): { text: string; truncated: boolean } {
  if (limit <= 0 || text.length <= limit) return { text, truncated: false };
  const head = text.slice(0, limit);
  const space = head.lastIndexOf(' ');
  let cut = space > Math.floor(limit * 0.6) ? head.slice(0, space) : head;
  while (cut.length > 0 && TRAILING_CUT_CHARS.includes(cut[cut.length - 1])) cut = cut.slice(0, cut.length - 1);
  return { text: `${cut}\u2026`, truncated: true };
}

/**
 * What one change's row renders as words, or NOTHING.
 *
 * Nothing is the important half again. A review-loop change keeps its localized label and loses its
 * text, so the row is "Track Yehuda — Review passed" and not a reviewer's 3,693-character verification
 * transcript. Everything else is the agent's own account of what changed, which is what the reader
 * wanted — stripped to prose and capped, with the rest one tap away.
 */
export function ledgerChangeText(change: WebLedgerChange, limit: number = LEDGER_TEXT_LIMIT): LedgerText | undefined {
  if (isReviewCorrespondence(change.label)) return undefined;
  const full = plainLedgerText(change.content);
  if (!full) return undefined;
  const clamped = clampLedgerText(full, limit);
  return { text: clamped.text, full, truncated: clamped.truncated };
}
